// components/ProjectTypeSelectionModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { ProjectType } from '#types/types';
import StoreSwapAnimation from './StoreSwapAnimation';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore, withMergedInventory } from '@/kernel/kernelStores';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';

const translations = {
  en: {
    title: 'Select Project Type',
    description: 'Choosing a project type helps our AI assistant provide more relevant guidance and tools for your goal.',
    storeSwapTitle: 'Store Swap',
    freeformTitle: 'Free Form',
    done: 'Done',
  },
  fr: {
    title: 'Sélectionner le type de projet',
    description: "Choisir un type de projet aide notre assistant IA à fournir des conseils et des outils plus pertinents pour votre objectif.",
    storeSwapTitle: 'Réaménagement de Magasin',
    freeformTitle: 'Format Libre',
    done: 'Terminé',
  },
};

interface ProjectTypeSelectionModalProps {
  lang?: 'en' | 'fr';
}

const ProjectTypeSelectionModal: React.FC<ProjectTypeSelectionModalProps> = ({ lang }) => {
  const { kernel } = useKernel();
  const storeLang = useKernelStore((state) => state.language);
  const projectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState);
  const storeProjectType = useKernelStore((state) => {
    const scopedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!scopedProjectId) return null;
    const mergedInventory = withMergedInventory(state);
    const projectType = mergedInventory.Project.registry[scopedProjectId]?.projectType;
    return projectType === 'FREEFORM' || projectType === 'STORE_SWAP'
      ? projectType
      : null;
  });
  const resolvedLang = lang ?? storeLang;
  const t = translations[resolvedLang];

  const [selectedType, setSelectedType] = useState<ProjectType | null>(storeProjectType);

  useEffect(() => {
    setSelectedType(storeProjectType);
  }, [storeProjectType]);

  const handleTypeSelect = useCallback((projectType: ProjectType) => {
    setSelectedType(projectType);
  }, []);

  const handleStoreSwapClick = useCallback(() => {
    handleTypeSelect('STORE_SWAP');
  }, [handleTypeSelect]);

  const handleFreeformClick = useCallback(() => {
    handleTypeSelect('FREEFORM');
  }, [handleTypeSelect]);

  const handleConfirm = useCallback(() => {
    if (!selectedType) return;
    const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
    if (!normalizedProjectId) return;

    kernel.current?.updateProjectDto(normalizedProjectId, { projectType: selectedType });
    kernel.current?.logUserAction('Project Options Set', { type: selectedType });
    kernel.current?.closeModal();
  }, [kernel, projectId, selectedType]);

  return (
    <div className="fixed inset-0 z-501 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-neutral-800 text-white rounded-lg shadow-xl w-full max-w-xl p-6 animate-fade-in overflow-y-auto max-h-[90vh] custom-scrollbar">
        <h2 className="text-xl font-semibold mb-3 text-center">{t.title}</h2>
        <p className="text-neutral-400 mb-6 text-center">{t.description}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div
            onClick={handleStoreSwapClick}
            className={`flex flex-col items-center justify-center p-4 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-800 focus:ring-neutral-400 cursor-pointer ${selectedType === 'STORE_SWAP' ? 'ring-2 ring-neutral-400' : ''}`}
          >
            <StoreSwapAnimation lang={resolvedLang} />

            <h3 className="text-base font-bold mt-3">{t.storeSwapTitle}</h3>
            <div className="h-3 mt-1"></div>
          </div>

          <div
            onClick={handleFreeformClick}
            className={`flex flex-col items-center justify-center p-4 bg-neutral-700 rounded-lg hover:bg-neutral-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-800 focus:ring-neutral-400 cursor-pointer ${selectedType === 'FREEFORM' ? 'ring-2 ring-neutral-400' : ''}`}
          >
            <h3 className="text-base font-bold mt-3">{t.freeformTitle}</h3>
            <div className="h-3 mt-1"></div>
          </div>
        </div>

        {selectedType && (
          <div className="mt-4 pt-4 border-t border-neutral-700 animate-fade-in">
            <div className="mt-2 flex justify-center">
              <button
                onClick={handleConfirm}
                className="px-6 py-2 text-base font-semibold text-white bg-neutral-600 hover:bg-neutral-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-800 focus:ring-neutral-400"
              >
                {t.done}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProjectTypeSelectionModal);

